"use client";

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Typography,
} from "@/components/ui";
import { SelectDropdownChevron } from "@/components/ui/shadcn/Select/Select";
import { cn } from "@/lib/utils/cn";
import { Check, PlusCircle } from "lucide-react";
import * as React from "react";

export type SelectOption = {
  group?: string;
  id?: string;
  value: string;
  label: string;
  context?: string;
};

export const groupSelectOptions = (options: SelectOption[]) => {
  const groups = new Map<string, SelectOption[]>();
  options.forEach((option) => {
    const group = option.group ?? "";
    groups.set(group, [...(groups.get(group) ?? []), option]);
  });
  return [...groups.entries()];
};

interface CreatableSelectProps {
  options: SelectOption[];
  placeholder?: string;
  emptyMessage?: string;
  value: string;
  onChange: (value: string) => void;
  createMessage?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  ariaLabel?: string;
}

const sanitizeOptions = (options: SelectOption[]): SelectOption[] =>
  options.filter(
    (option): option is SelectOption =>
      typeof option.value === "string" &&
      typeof option.label === "string" &&
      option.value.trim().length > 0 &&
      option.label.trim().length > 0,
  );

export function CreatableSelect({
  options: initialOptions,
  placeholder = "Select an option",
  emptyMessage = "No options found.",
  value,
  onChange,
  createMessage = "Create",
  className,
  disabled = false,
  label,
  ariaLabel,
}: CreatableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<SelectOption[]>(() =>
    sanitizeOptions(initialOptions),
  );

  // Update options if initialOptions change
  React.useEffect(() => {
    setOptions(sanitizeOptions(initialOptions));
  }, [initialOptions]);

  // Find the selected option
  const selectedOption = options.find((option) => option.value === value);

  // Check if the input value matches any existing option
  const matchingOption = options.find(
    (option) =>
      typeof option.value === "string" &&
      option.value.toLowerCase() === inputValue.toLowerCase(),
  );

  // Handle creating a new option
  const handleCreateOption = (input: string) => {
    if (!input.trim()) return;

    const newOption = { value: input, label: input };

    // Add the new option if it doesn't already exist
    if (!options.some((option) => option.value === input)) {
      setOptions((prev) => [...prev, newOption]);
    }

    // Select the new option
    onChange(input);
    setInputValue("");
    setOpen(false);
  };

  // Handle selecting an option
  const handleSelectOption = (selectedValue: string) => {
    onChange(selectedValue);
    setInputValue("");
    setOpen(false);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && <div className="text-sm font-medium">{label}</div>}
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-label={ariaLabel}
            aria-expanded={open}
            className={cn(
              "border-border/50 group flex w-full items-center justify-between gap-2",
              !value && "text-muted-foreground",
            )}
            disabled={disabled}
          >
            <Typography className="line-clamp-1">
              {value ? (selectedOption?.label ?? value) : placeholder}
            </Typography>
            <SelectDropdownChevron />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          onEscapeKeyDown={() => {
            setOpen(false);
          }}
          className="w-[--radix-popover-trigger-width] min-w-[80vw] p-0 md:min-w-[400px]"
        >
          <Command>
            <CommandInput
              placeholder={placeholder}
              value={inputValue}
              onValueChange={setInputValue}
              onKeyDown={(event) => {
                if (event.key === "Enter" && event.nativeEvent.isComposing) {
                  event.stopPropagation();
                }
              }}
            />
            <CommandList>
              <CommandEmpty>
                {inputValue.trim() ? (
                  <button
                    className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
                    onClick={() => {
                      handleCreateOption(inputValue);
                    }}
                  >
                    <PlusCircle className="h-4 w-4" />
                    <span>
                      {createMessage}
                      <span className="font-medium">{`"${inputValue}"`}</span>
                    </span>
                  </button>
                ) : (
                  emptyMessage
                )}
              </CommandEmpty>
              {groupSelectOptions(options).map(
                ([group, groupedOptions], groupIndex) => (
                  <CommandGroup
                    key={group || "options"}
                    heading={group || undefined}
                    className={cn(groupIndex > 0 && "border-t pt-1")}
                  >
                    {groupedOptions.map((option) => (
                      <CommandItem
                        key={option.id ?? option.value}
                        value={option.id ?? option.value}
                        keywords={[
                          option.label,
                          option.value,
                          option.context ?? "",
                        ]}
                        onSelect={() => {
                          handleSelectOption(option.value);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === option.value
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        <div className="flex flex-col">
                          {option.label}
                          {option.context && (
                            <span className="text-muted-foreground text-sm">
                              {option.context}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ),
              )}
              {inputValue.trim() && !matchingOption && (
                <CommandGroup className="border-t pt-1">
                  <CommandItem
                    value={inputValue}
                    onSelect={() => {
                      handleCreateOption(inputValue);
                    }}
                  >
                    <PlusCircle className="mr-2 h-4 w-4" />
                    {`${createMessage} "${inputValue}"`}
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
